import { DEFAULT_SIDEBAR_WIDTH, SETTINGS_HEIGHT, TIMELINE_MIN_HEIGHT, ZOOM_MODE_DAY } from "../../helpers/Const";
import React, { Component } from "react";
import { DragLayer } from "react-dnd";
import PropertyTimeLine from "./PropertyTimeLine";
import SettingsPanel from "../settings/SettingsPanel";
import TaskTimeLine from "./TaskTimeLine";
import { differenceInCalendarDays, differenceInHours } from "date-fns";
import { dragLayerCollect } from "../../helpers/DragAndDrop";
import { getChartTotalWidth } from "../../helpers/StyleHelpers";
import styled from "styled-components/macro";
import { ViewType } from "../../../backend/constants/viewTypes";
import { getRootLevelData } from "../../helpers/GraphUtils";
import { IViewConfigurationItem } from "src/backend/interfaces/configuration/IConfiguration";
import { IHierarchyItem } from "src/backend/interfaces/IItem";
import { withMsal } from "@azure/msal-react";
import { WithMsalProps } from "@azure/msal-react/dist/components/withMsal";
import { ITimeLineCommonProps } from "./TimeLineCommon";
import { addQueryParameter, parseSideBarWidthFromQueryString } from "src/backend/helpers/urlHelper";
import { QUERYSTRING_SIDEBAR_WIDTH } from "src/backend/constants/common";

const ProgramContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  font-size: 14px;
`;

const TimeLineContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

interface ITimeLineHubProps extends WithMsalProps {
  hourWidth: number;
  headerElem: JSX.Element;
  mouseXDelta: number;
  mouseYDelta: number;
  startDate: Date;
  endDate: Date;
  viewConfigurations: IViewConfigurationItem[];
  viewPortWidth: number;
  zoomMode: string;
  dragTaskId: number;
  dragType: string;
  loading: boolean;
  viewData: IHierarchyItem[][];
  dayClicked: Date;
  onStartDrag: (e: React.MouseEvent<HTMLElement>, taskId: number, dragType: string) => void;
  dndDragUnderway: boolean;
  onHorizontalResize: (totalWidth: number) => void;
  onEndDrag: (taskId: number, viewIndex: number, dragType: string, extraData: number, isCancelled: boolean) => void;
  onScrollToClickedDayEnd: (scrollEnd: boolean) => void;
}

interface ITimeLineHubState {
  scrollLeft: number;
  scrollLeftOnDragStart: number;
  sideBarWidth: number;
  timelineHeights: number[];
}

class TimeLineHub extends Component<ITimeLineHubProps,ITimeLineHubState> {
  constructor(props: ITimeLineHubProps) {
    super(props);
    const visibleHours = this.getNumVisibleHours();
    const hourDiffToToday = Math.floor(differenceInHours(new Date(), this.props.startDate) - visibleHours / 2);

    let sideBarWidth = DEFAULT_SIDEBAR_WIDTH;

    let query = parseSideBarWidthFromQueryString();

    if ( query ) {
      sideBarWidth = parseInt( query );
    }

    this.state = {
      scrollLeft: Math.floor(this.props.hourWidth * hourDiffToToday),
      scrollLeftOnDragStart: 0,
      sideBarWidth: sideBarWidth,
      timelineHeights: this.getInitialTimelineHeights(),
    };
  }

  componentDidMount = () => {
    window.addEventListener("resize", this.onTimelineAreaVerticalResize);
  }; 

  componentWillUnmount = () => {
    window.removeEventListener("resize", this.onTimelineAreaVerticalResize);
  };

  getInitialTimelineHeights = () => {
    const timelineInitialHeight = (window.innerHeight - SETTINGS_HEIGHT) / this.props.viewConfigurations.length;
    const heightsList: number[] = [];
    for (const _ of this.props.viewConfigurations) {
      heightsList.push(timelineInitialHeight);
    }
    return heightsList;
  };

  scrollToCurrentDay = () => {
    const visibleHours = this.getNumVisibleHours();
    const hourDiffToToday = Math.floor(differenceInHours(new Date(), this.props.startDate) - visibleHours / 2);
    this.setState(() => ({
      scrollLeft: Math.floor(this.props.hourWidth * hourDiffToToday),
    }));
  };

  scrollToClickedDay = () => {
    // const visibleHours   = this.getNumVisibleHours();
    const hourDiffToDate = Math.floor(differenceInHours(this.props.dayClicked, this.props.startDate));

    this.setState(() => ({
      scrollLeft: Math.floor(this.props.hourWidth * hourDiffToDate),
    }));

    this.props.onScrollToClickedDayEnd(true)
  };


  /////////////////////////
  //   VIEWPORT CHANGES  //
  /////////////////////////

  onHorizontalScroll = (newScrollLeft: number) => {
    if (newScrollLeft !== this.state.scrollLeft) {
      this.setState({
        scrollLeft: newScrollLeft,
      });
    }
  };

  onTaskListSizing = (delta: number) => {
    this.setState((prevState) => {
      let newWidth = prevState.sideBarWidth - delta;
      // 1000 is a rough estimate of the settings buttons to the right side of the resizer bar
      newWidth = Math.max(300, Math.min(newWidth, window.innerWidth - 1000));

      // set to URL
      const url = addQueryParameter(window.location.href, QUERYSTRING_SIDEBAR_WIDTH, newWidth.toString());
      window.history.replaceState({}, "", url);

      return { sideBarWidth: newWidth };
    });
  };

  getCurrentDayIndex = (hourWidth: number, scrollLeft: number) => {
    return Math.trunc(scrollLeft / (hourWidth * 24));
  };

  getNumVisibleHours = () => {
    return Math.ceil(this.props.viewPortWidth / this.props.hourWidth);
  };

  renderView = (viewProps: ITimeLineCommonProps) => {
    if (viewProps.viewType === ViewType.Scheduling) {
      return <TaskTimeLine key={viewProps.viewIndex} {...viewProps} />;
    } else if (viewProps.viewType === ViewType.Resourcing) {
      return <PropertyTimeLine key={viewProps.viewIndex} {...viewProps} />;
    }
    return null;
  };

  onStartDrag = (e, taskId, dragType) => {
    this.setState(() => {
      return { scrollLeftOnDragStart: this.state.scrollLeft };
    });
    this.props.onStartDrag(e, taskId, dragType);
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (
      prevProps.startDate !== this.props.startDate ||
      prevProps.endDate !== this.props.endDate ||
      prevProps.zoomMode !== this.props.zoomMode ||
      prevProps.hourWidth !== this.props.hourWidth
    ) {
      const startDateDiff = differenceInCalendarDays(prevProps.startDate, this.props.startDate);
      const previousDayIndex = this.getCurrentDayIndex(prevProps.hourWidth, prevState.scrollLeft);
      let newScrollLeft = this.props.hourWidth * 24 * (previousDayIndex + startDateDiff);

      if ( this.props.zoomMode === ZOOM_MODE_DAY && this.props.dayClicked ) {

        this.scrollToClickedDay();
        
      } else {
        this.setState(() => {
          return {
            scrollLeft: newScrollLeft,
          };
        });
      }
    }

    if (prevProps.viewConfigurations.length !== this.props.viewConfigurations.length) {
      this.setState(() => ({
        timelineHeights: this.getInitialTimelineHeights(),
      }));
    }
  };

  onTimelineVerticalResizerDrag = (delta: number, index: number) => {
    // TODO: Allow squashing viewports beyond the ones affected by the current drag.
    // For example, if there are 3 viewports, with heights 200, 200, 400, the current
    // setup doesn't allow growing the first one, even though it could use the space
    // of the third viewport. Now you gotta first resize the second one: 200, 300, 300
    // and only then you can resize the first one: 250, 250, 300.
    const newHeights = [...this.state.timelineHeights];

    //Ensure that the viewports aren't resized too small.
    if (delta > 0) {
      delta = Math.min(delta, newHeights[index] - TIMELINE_MIN_HEIGHT);
    } else {
      delta = Math.max(delta, TIMELINE_MIN_HEIGHT - newHeights[index + 1]);
    }

    newHeights[index] -= delta;
    newHeights[index + 1] += delta;

    this.setState(() => ({
      timelineHeights: newHeights,
    }));
  };

  onTimelineAreaVerticalResize = () => {
    const newTimelineAreaHeight = window.innerHeight - SETTINGS_HEIGHT;
    const newHeights = [];
    let totalPreviousTimelineHeight = 0;
    for (const height of this.state.timelineHeights) {
      totalPreviousTimelineHeight += height;
    }
    const scalingFactor = newTimelineAreaHeight / totalPreviousTimelineHeight;

    let heightDebt = 0; // This symbolises the amount of pixels we need to cut after the next pass in order to fit the timelines into the total area.
    for (const height of this.state.timelineHeights) {
      let newHeight = Math.floor(height * scalingFactor);
      if (newHeight < TIMELINE_MIN_HEIGHT) {
        heightDebt += TIMELINE_MIN_HEIGHT - newHeight;
        newHeight = TIMELINE_MIN_HEIGHT;
      }
      newHeights.push(newHeight);
    }

    for (let i = 0; i < newHeights.length; i++) {
      const amountToCut = Math.min(heightDebt, newHeights[i] - TIMELINE_MIN_HEIGHT);
      newHeights[i] -= amountToCut;
      heightDebt -= amountToCut;
    }

    this.setState(() => ({
      timelineHeights: newHeights,
    }));
  };

  setTimelineHeights = (newHeights) => {
    this.setState(() => ({
      timelineHeights: newHeights,
    }));
  };

  setSidebarWidth = (newWidth) => {
    this.setState(() => ({
      sideBarWidth: newWidth,
    }));
  }

  render() {
    //This prop comes from the drag and drop injection.
    const { dndDragUnderway } = this.props;
    const dragUnderwayPreliminary = this.props.dragTaskId !== null || dndDragUnderway;

    const chartTotalWidth = getChartTotalWidth(this.props.hourWidth, this.props.startDate, this.props.endDate);

    return (
      <ProgramContainer>
        <SettingsPanel
          sideBarWidth={this.state.sideBarWidth}
          zoomMode={this.props.zoomMode}
          hourWidth={this.props.hourWidth}
          startDate={this.props.startDate}
          endDate={this.props.endDate}
          scrollToCurrentDay={this.scrollToCurrentDay}
          getTimelineHeights={this.state.timelineHeights}
          setTimelineHeights={this.setTimelineHeights}
          setSidebarWidth={this.setSidebarWidth}
        ></SettingsPanel>
        <TimeLineContainer id="timeline-container">
          {this.props.viewConfigurations.map((vc, index) => {
            const viewProps: ITimeLineCommonProps = {
              hourWidth: this.props.hourWidth,
              headerElem: this.props.headerElem,
              mouseXDelta: this.props.mouseXDelta,
              mouseYDelta: this.props.mouseYDelta,
              dragTaskId: this.props.dragTaskId,
              dragType: this.props.dragType,
              dragUnderwayPreliminary: dragUnderwayPreliminary,
              scrollLeftOnDragStart: this.state.scrollLeftOnDragStart,
              scrollLeft: this.state.scrollLeft,
              onTaskListSizing: this.onTaskListSizing,
              onHorizontalScroll: this.onHorizontalScroll,
              sideBarWidth: this.state.sideBarWidth,
              startDate: this.props.startDate,
              endDate: this.props.endDate,
              chartTotalWidth: chartTotalWidth,
              onHorizontalResize: this.props.onHorizontalResize,
              //onTogglePropertyExpand: this.togglePropertyExpand,
              //onToggleTaskByPropertyExpand: this.toggleTaskByPropertyExpand,
              onStartDrag: this.onStartDrag,
              onEndDrag: this.props.onEndDrag,
              zoomMode: this.props.zoomMode,
              //onToggleTaskExpand: this.toggleTaskExpand,
              loading: this.props.loading,
              onTimelineVerticalResizerDrag: this.onTimelineVerticalResizerDrag,
              allData: this.props.viewData[index],
              rootData: getRootLevelData(vc, this.props.viewData[index]),
              viewType: vc.viewType,
              viewIndex: index,
              timelineTotalHeight: this.state.timelineHeights[index],
              isLast: index === this.props.viewConfigurations.length - 1,
            };
            return this.renderView(viewProps);
          })}
        </TimeLineContainer>
      </ProgramContainer>
    );
  }
}

export default withMsal(DragLayer(dragLayerCollect)(TimeLineHub));
